import React, {
  createContext,
  MutableRefObject,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react"
import config from "react-global-configuration"
import { connect } from "react-redux"
import {
  connect as connectTwilio,
  LocalAudioTrack,
  LocalTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteTrack,
  RemoteVideoTrack,
  Room,
  StatsReport,
  TwilioError,
} from "twilio-video"

import { languages } from "lib/languages"
import { ActionWithoutPayload } from "types/redux"

import { getCall } from "../services/actions"
import {
  RoomState,
} from "../types/types"
import { createUserLocalTracks } from "../utils"
import styles from "./../Call.module.scss"

export type ConnectParams = {
  tool_token?: string
  token: string
  roomName: string
}

export type VideoContextProps = {
  connect: (params: ConnectParams) => void
  disconnect: () => void
  connected: boolean
  mediaProblemDetected: boolean
  state: RoomState
  children?: ReactNode
}

// Twilio maps the underlying WebRTC states
// (https://w3c.github.io/webrtc-pc/#rtcpeerconnectionstate-enum,
// #rtcsignalingstate-enum, #rtciceconnectionstate-enum) onto Room events:
// "reconnecting" fires with 53001/53002 when the signaling transport drops
// and with 53405 when the media transport reaches RTCIceConnectionState
// "failed"; "disconnected" fires with the same codes once the SDK's own
// ICE restarts / signaling resumes are exhausted. Only those codes are
// worth a client-side rejoin — the others (room completed, duplicate
// identity, invalid token...) are terminal.
const RECOVERABLE_DISCONNECT_CODES = [53000, 53001, 53002, 53405]
const MAX_REJOIN_ATTEMPTS = 5
const REJOIN_BASE_DELAY_MS = 2000

// Consecutive bad checks of the terminal's own camera/mic before the patient
// is told to refresh. Long enough not to flash the modal during a rejoin.
const MEDIA_PROBLEM_DELAY_S = 15
const MEDIA_CHECK_INTERVAL_MS = 1000

// While one of these is showing, the rejoin layer is already telling the
// patient a reconnection is under way and is retrying on its own
const RECONNECTING_STATES = [
  RoomState.RECONNECTION,
  RoomState.CONNECTION_LOST,
  RoomState.PEER_CONNECTION_LOST,
]

const initialProps = {
  state: RoomState.ROOM_CONNECTION,
  connected: false,
  mediaProblemDetected: false,
}

const VideoContext = createContext<VideoContextProps>(
  initialProps as VideoContextProps
)
const VideoProvider = (
  props: React.PropsWithChildren<{ getCurrentCall: () => void }>
) => {
  const [state, setState] = useState(RoomState.ROOM_CONNECTION)
  const [connected, setConnected] = useState(false)
  const localMedia = useRef<HTMLDivElement>(null)
  const remoteMedia = useRef<HTMLDivElement>(null)

  const patientRoomRef = useRef<Room | null>(null)
  const toolRoomRef = useRef<Room | null>(null)
  const localTracksRef = useRef<LocalTrack[]>([])
  const connectParamsRef = useRef<ConnectParams | null>(null)
  const rejoinAttemptsRef = useRef(0)
  const rejoinTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const waitingForNetworkRef = useRef(false)
  const intentionalDisconnectRef = useRef(false)

  useEffect(() => {
    const onOnline = () => {
      if (waitingForNetworkRef.current) {
        waitingForNetworkRef.current = false
        rejoinPatientRoom()
      }
    }
    window.addEventListener("online", onOnline)
    return () => {
      window.removeEventListener("online", onOnline)
      intentionalDisconnectRef.current = true
      clearRejoinTimer()
      clearMediaCheckInterval()
      toolRoomRef.current?.disconnect()
      patientRoomRef.current?.disconnect()
    }
  }, [])

  const clearRejoinTimer = () => {
    if (rejoinTimerRef.current) {
      clearTimeout(rejoinTimerRef.current)
      rejoinTimerRef.current = null
    }
  }

  const clearMediaContainer = (div: RefObject<HTMLDivElement>) => {
    if (div.current) div.current.innerHTML = ""
  }

  const stopLocalTracks = () => {
    localTracksRef.current.forEach((track) => {
      if (track.kind === "audio" || track.kind === "video") {
        track.stop()
        track.detach().forEach((element: HTMLMediaElement) => element.remove())
      }
    })
    localTracksRef.current = []
  }

  const [mediaProblemDetected, setMediaProblemDetected] = useState(false)
  const mediaCheckInterval = useRef<ReturnType<typeof setInterval>>()
  const mediaProblemCounter = useRef(0)
  const checkingMedia = useRef(false)
  // Watching only makes sense once the TLC has actually started; before the
  // doctor's first join the patient is simply in the waiting room
  const doctorJoined = useRef(false)
  const prevLocalVideoPackets = useRef<number | null>(null)
  // checkMediaStreams runs from an interval created in roomEvent, which would
  // capture a stale `state`; the ref keeps the current one readable there
  const roomStateRef = useRef(state)

  useEffect(() => {
    roomStateRef.current = state
  }, [state])

  const resetMediaProblemDetection = () => {
    mediaProblemCounter.current = 0
    prevLocalVideoPackets.current = null
    setMediaProblemDetected(false)
  }

  const clearMediaCheckInterval = () => {
    if (mediaCheckInterval.current) {
      clearInterval(mediaCheckInterval.current)
      mediaCheckInterval.current = undefined
    }
  }

  // A track whose packet count stops growing is no longer carrying media
  const packetsStalled = (
    packets: number | null,
    prevPackets: MutableRefObject<number | null>
  ): boolean => {
    if (packets == null) return false
    const stalled =
      prevPackets.current !== null && packets <= prevPackets.current
    prevPackets.current = packets
    return stalled
  }

  // Only the terminal's own camera/mic is watched: transport and doctor-side
  // problems belong to the rejoin layer, which retries by itself — a refresh
  // can only genuinely fix a local device. A stopped track, an ended device
  // (unplugged) or a muted one (device grabbed elsewhere, OS privacy shutter)
  // all mean nothing is being sent any more.
  const isLocalTrackFaulty = (
    track: LocalAudioTrack | LocalVideoTrack
  ): boolean =>
    track.isStopped ||
    track.isMuted ||
    track.mediaStreamTrack?.readyState === "ended" ||
    track.mediaStreamTrack?.muted === true

  // Outbound stall is only checked on video: audio packets legitimately stop
  // while the patient is silent, so a flat packetsSent there would flag silence
  // as a fault. Local stats are matched on trackId, local tracks have no sid.
  const isLocalVideoStalled = (
    track: LocalVideoTrack,
    reports: StatsReport[]
  ): boolean => {
    const stats = reports
      .flatMap((report) => report.localVideoTrackStats)
      .find((trackStats) => trackStats.trackId === track.id)
    if (!stats) return false
    return packetsStalled(stats.packetsSent, prevLocalVideoPackets)
  }

  // Only complain about media once the connection is nominally healthy: a
  // reconnection in progress, a rejoin waiting on its timer or on the network,
  // or a room that has already been replaced are all cases the rejoin layer
  // owns, and telling the patient to refresh over it would contradict it
  const isReconnecting = (_room: Room): boolean =>
    RECONNECTING_STATES.includes(roomStateRef.current) ||
    rejoinTimerRef.current !== null ||
    waitingForNetworkRef.current ||
    patientRoomRef.current !== _room

  const hasMediaProblem = async (_room: Room): Promise<boolean> => {
    // The tool participant joins the room through its own connection, so these
    // publications are always the patient's own camera and mic. They are read
    // fresh on every check rather than cached from events, so a track that
    // disappears is caught even if its event was missed.
    const localParticipant = _room.localParticipant
    const localVideoTrack =
      Array.from(localParticipant.videoTracks.values())[0]?.track || null
    const localAudioTrack =
      Array.from(localParticipant.audioTracks.values())[0]?.track || null
    if (!localVideoTrack || !localAudioTrack) return true
    if (
      isLocalTrackFaulty(localVideoTrack) ||
      isLocalTrackFaulty(localAudioTrack)
    ) {
      return true
    }
    let reports: StatsReport[] = []
    try {
      reports = await _room.getStats()
    } catch {
      return false
    }
    return isLocalVideoStalled(localVideoTrack, reports)
  }

  const checkMediaStreams = async (_room: Room) => {
    if (checkingMedia.current) return
    checkingMedia.current = true
    try {
      const problem =
        doctorJoined.current &&
        !isReconnecting(_room) &&
        (await hasMediaProblem(_room))
      if (problem) {
        mediaProblemCounter.current += 1
        if (mediaProblemCounter.current >= MEDIA_PROBLEM_DELAY_S) {
          setMediaProblemDetected(true)
        }
      } else {
        mediaProblemCounter.current = 0
        setMediaProblemDetected(false)
      }
    } finally {
      checkingMedia.current = false
    }
  }

  const connectTools = async ({
    roomName,
    tool_token,
  }: {
    roomName: string
    tool_token: string
  }) => {
    console.log("connecting tool to the room...", roomName)
    return connectTwilio(tool_token, {
      name: roomName,
      tracks: [],
    })
      .then((room) => {
        toolRoomRef.current = room
        room.once("disconnected", (_room: Room, error?: TwilioError) => {
          toolRoomRef.current = null
          if (
            !intentionalDisconnectRef.current &&
            error &&
            RECOVERABLE_DISCONNECT_CODES.includes(error.code)
          ) {
            console.error(
              `Tool room disconnected (${error.code}), rejoining`, {
              route: "twilio::connectTools::disconnected"
            })
            connectTools({ roomName, tool_token })
          }
        })
      })
      .catch((e) => console.error(e, {
        route: "twilio::connectTools::connectTwilio"
      }))
  }

  const connect = async ({ token, tool_token, roomName }: ConnectParams) => {
    console.log("connecting to the room...", roomName)
    connectParamsRef.current = { token, tool_token, roomName }
    intentionalDisconnectRef.current = false
    rejoinAttemptsRef.current = 0
    setConnected(true)
    setState(RoomState.ROOM_CONNECTING)
    clearMediaCheckInterval()
    doctorJoined.current = false
    resetMediaProblemDetection()

    tool_token && (await connectTools({ tool_token, roomName }))

    return await createUserLocalTracks()
      .then((localTracks: Array<LocalTrack>) => {
        localTracksRef.current = localTracks
        const videoTrack = localTracks.find((x) => x.kind === "video")
        trackSubscribed(videoTrack as LocalVideoTrack, localMedia)
        return connectTwilio(token, {
          name: roomName,
          tracks: localTracks,
          networkQuality: { local: 1, remote: 1 }
        })
      })
      .then((room) => {
        roomEvent(room)
      })
      .catch((e) => {
        console.error(e, {
          route: "twilio::connect::createUserLocalTracks"
        })
        scheduleRejoin()
      })
  }

  const disconnect = () => {
    intentionalDisconnectRef.current = true
    waitingForNetworkRef.current = false
    clearRejoinTimer()
    setConnected(false)
    clearMediaCheckInterval()
    resetMediaProblemDetection()
    toolRoomRef.current?.disconnect()
    patientRoomRef.current?.disconnect()
  }

  const getRejoinTracks = async (): Promise<LocalTrack[]> => {
    const current = localTracksRef.current
    const isLive = (track: LocalTrack) =>
      (track.kind === "audio" || track.kind === "video") && !track.isStopped
    if (current.length && current.every(isLive)) return current

    stopLocalTracks()
    clearMediaContainer(localMedia)
    const localTracks = await createUserLocalTracks()
    localTracksRef.current = localTracks
    const videoTrack = localTracks.find((x) => x.kind === "video")
    trackSubscribed(videoTrack as LocalVideoTrack, localMedia)
    return localTracks
  }

  const rejoinPatientRoom = async () => {
    const params = connectParamsRef.current
    if (!params || intentionalDisconnectRef.current) return
    setState(RoomState.RECONNECTION)
    console.log(
      `rejoining the room... (attempt ${rejoinAttemptsRef.current}/${MAX_REJOIN_ATTEMPTS})`,
      params.roomName
    )
    try {
      if (params.tool_token && !toolRoomRef.current) {
        await connectTools({
          tool_token: params.tool_token,
          roomName: params.roomName,
        })
      }
      const localTracks = await getRejoinTracks()
      const room = await connectTwilio(params.token, {
        name: params.roomName,
        tracks: localTracks,
        networkQuality: { local: 1, remote: 1 }
      })
      roomEvent(room)
    } catch (e) {
      console.error(e, {
        route: "twilio::rejoinPatientRoom"
      })
      scheduleRejoin()
    }
  }

  const scheduleRejoin = () => {
    if (intentionalDisconnectRef.current) return
    if (rejoinAttemptsRef.current >= MAX_REJOIN_ATTEMPTS) {
      console.error("Unable to rejoin the room, giving up", {
        route: "twilio::scheduleRejoin"
      })
      stopLocalTracks()
      setState(RoomState.ERROR)
      disconnect()
      props.getCurrentCall()
      return
    }
    if (!navigator.onLine) {
      // The device itself is offline: ICE gathering/checking cannot succeed,
      // so wait for the browser "online" event instead of burning attempts.
      waitingForNetworkRef.current = true
      setState(RoomState.CONNECTION_LOST)
      return
    }
    const delay = REJOIN_BASE_DELAY_MS * 2 ** rejoinAttemptsRef.current
    rejoinAttemptsRef.current += 1
    clearRejoinTimer()
    rejoinTimerRef.current = setTimeout(() => {
      rejoinTimerRef.current = null
      rejoinPatientRoom()
    }, delay)
  }

  const roomEvent = (_room: Room) => {
    patientRoomRef.current = _room
    rejoinAttemptsRef.current = 0
    setState(
      _room.participants.size > 0
        ? RoomState.LINKING_PEER
        : RoomState.WAITING_PEER
    )

    _room.participants.forEach(participantEvent)

    clearMediaCheckInterval()
    mediaCheckInterval.current = setInterval(
      () => void checkMediaStreams(_room),
      MEDIA_CHECK_INTERVAL_MS
    )

    _room.on("participantConnected", participantEvent)

    _room.on("participantDisconnected", (participant) => {
      console.warn(`Participant "${participant.identity}" disconnected`)
    })

    _room.localParticipant.on(
      "trackPublicationFailed",
      (error: TwilioError, track: LocalTrack) => {
        console.error(
          `Track publication failed (${track.kind}): ${error?.code} ${error?.message}`,
          { route: "videoContext::roomEvent::trackPublicationFailed" },
        )
      },
    )

    _room.localParticipant.on(
      "networkQualityLevelChanged",
      (level: number) => {
        if (level <= 1) console.error(`Low network quality level: ${level}`, { route: 'videoContext::networkQualityLevelChanged' })
      },
    )

    _room.on("reconnecting", (error: TwilioError) => {
      // 53001/53002: signaling transport disrupted; 53405: media transport
      // reached RTCIceConnectionState "failed". The SDK retries by itself
      // (ICE restart / signaling resume) — only reflect it in the UI here.
      console.warn(`Room reconnecting (${error?.code}): ${error?.message}`)
      setState(RoomState.RECONNECTION)
    })

    _room.on("reconnected", () => {
      console.warn("Room reconnected")
      setState(
        _room.participants.size > 0
          ? RoomState.LINKING_PEER
          : RoomState.WAITING_PEER
      )
    })

    _room.once("disconnected", (room: Room, error?: TwilioError) => {
      console.warn(`[Bye bye] Room disconnected`)
      patientRoomRef.current = null
      if (error) {
        console.log(
          "You were disconnected from the Room:",
          error.code,
          error.message
        )
      }

      const canRejoin =
        !intentionalDisconnectRef.current &&
        error &&
        RECOVERABLE_DISCONNECT_CODES.includes(error.code)

      if (canRejoin) {
        // Keep the local tracks alive so the patient preview survives the
        // rejoin; only drop the now-frozen remote stream.
        clearMediaContainer(remoteMedia)
        setState(RoomState.CONNECTION_LOST)
        scheduleRejoin()
        return
      }

      stopLocalTracks()
      disconnect()
      props.getCurrentCall()
    })
  }

  const participantEvent = (participant: Participant) => {
    console.warn(`Participant "${participant.identity}" connected`)

    if (participant.identity.includes("doctor")) doctorJoined.current = true

    participant.on(
      "trackSubscribed",
      (track: RemoteVideoTrack | RemoteAudioTrack) =>
        participant.identity.includes("doctor") &&
        trackSubscribed(track, remoteMedia)
    )

    participant.on("trackUnsubscribed", (e) => trackUnsubscribed(e))

    if (participant.identity.includes("doctor")) {
      participant.on("reconnecting", () => {
        console.warn(`Participant "${participant.identity}" reconnecting`)
        setState(RoomState.PEER_CONNECTION_LOST)
      })

      participant.on("reconnected", () => {
        console.warn(`Participant "${participant.identity}" reconnected`)
        setState(RoomState.LINKING_PEER)
      })
    }
  }

  const trackSubscribed = (
    track: RemoteTrack | LocalTrack,
    div: RefObject<HTMLDivElement>
  ) => {
    track.kind !== "data" && div.current?.appendChild(track.attach())
  }

  const trackUnsubscribed = (track: RemoteTrack | LocalTrack) => {
    track.kind !== "data" &&
      track.detach().forEach((element: HTMLMediaElement) => element.remove())
  }

  const reconnectionMessage = {
    [RoomState.CONNECTION_LOST]: languages.callConnectionLost,
    [RoomState.RECONNECTION]: languages.callReconnecting,
    [RoomState.PEER_CONNECTION_LOST]: languages.callPeerReconnecting,
  }[state]

  return (
    <VideoContext.Provider
      value={{
        connect,
        disconnect,
        state,
        connected,
        mediaProblemDetected,
      }}
    >
      <div id={styles.CallContainer}>
        <div className={styles.RemoteContainer}>
          <div
            id={styles.RemoteMedia}
            className={`${styles.LoadingBackgroundVideo}`}
            ref={remoteMedia}
          />
          {reconnectionMessage && (
            <div className={styles.ReconnectionBanner}>
              {reconnectionMessage}
            </div>
          )}
          <div className={styles.ChildrenContainer}>{props.children}</div>
        </div>
        <div>
          <div
            id={styles.LocalMedia}
            className={styles.LoadingBackgroundVideo}
            ref={localMedia}
          />
          <div className={styles.HelpSupport}>
            Support : {config.get("support")}
          </div>
        </div>
      </div>
    </VideoContext.Provider>
  )
}

export { VideoContext }

const mapDispatchToProps = (dispatch: (a: ActionWithoutPayload) => void) => {
  return {
    getCurrentCall: () => dispatch(getCall()),
  }
}
export default connect(null, mapDispatchToProps)(VideoProvider as any) as any
